/**
 * Workflow — the six BPMN figures of the live grading process, each a figure card (ops mock versions 9–13): a separator
 * rule, a header band with the figure's title, Purpose and Process, the image, the caption; click to open it full size
 * in the lightbox (fit, zoom to 300 %, drag to pan, prev / next, Escape or the circle close).
 *
 * ⭐ THE WORDS ARE CONTENT, NOT CODE (version 11 §B1). Title, Purpose and Process come from the community service's
 * content records (falling back to the reference's text there), and super_admin edits them in place — the title one
 * line, Purpose and Process 25 words or fewer — written to every zone so the pool reads one set. The SVGs are static
 * assets loaded by the path the service names, so a later Regenerate can drop in new ones; they are not editable here.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast, toastFromError } from '../../../components/ToastProvider';
import { ZONES, type PortalZone } from '../../../lib/zones';
import { FIGURE_WORD_LIMIT, getWorkflow, putFigure, wordsOf, type Figure } from '../../../lib/grading-api';
import { ActionIcon, Loading, fmtDate, type PageCtx } from './shared';

const figLabel = (f: Figure) => `figure ${f.figure}`;

function Editor({ fig, onCancel, onSave, busy }: { fig: Figure; busy: boolean; onCancel: () => void; onSave: (v: { title: string; purpose: string; process: string }) => void }) {
  const [v, setV] = useState({ title: fig.title, purpose: fig.purpose, process: fig.process });
  const n = { purpose: wordsOf(v.purpose), process: wordsOf(v.process) };
  const ok = !!v.title.trim() && !/\n/.test(v.title) && n.purpose > 0 && n.purpose <= FIGURE_WORD_LIMIT && n.process > 0 && n.process <= FIGURE_WORD_LIMIT;
  return (
    <div className="wf-editor" data-testid={`wf-editor-${fig.figure}`}>
      <label><span className="lh">Title <span className="wf-count">one line</span></span>
        <input type="text" maxLength={120} value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} aria-label="Title" /></label>
      {(['purpose', 'process'] as const).map((k) => (
        <label key={k}><span className="lh">{k === 'purpose' ? 'Purpose' : 'Process'} <span className={`wf-count${n[k] > FIGURE_WORD_LIMIT ? ' over' : ''}`}>{n[k]} / {FIGURE_WORD_LIMIT} words</span></span>
          <textarea rows={2} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} aria-label={k === 'purpose' ? 'Purpose' : 'Process'} /></label>
      ))}
      <div className="acts">
        <button type="button" className="portal-btn" disabled={!ok || busy} onClick={() => onSave({ title: v.title.trim(), purpose: v.purpose.trim(), process: v.process.trim() })}>{busy ? 'Saving…' : 'Save'}</button>
        <button type="button" className="portal-link-button-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
        <span className="note">Words only — the diagram stays. Stored per figure; no release.</span>
      </div>
    </div>
  );
}

export function Lightbox({ figures, index, onIndex, onClose }: { figures: Figure[]; index: number; onIndex: (i: number) => void; onClose: () => void }) {
  const f = figures[index]!;
  const stage = useRef<HTMLDivElement>(null);
  const img = useRef<HTMLImageElement>(null);
  const close = useRef<HTMLButtonElement>(null);
  const [z, setZ] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [size, setSize] = useState({ w: 0, h: 0, fit: 1 });
  const drag = useRef<{ x: number; y: number } | null>(null);
  const zoom = useCallback((n: number) => setZ(Math.max(1, Math.min(3, Math.round(n * 100) / 100))), []);
  const fitNow = useCallback(() => {
    const st = stage.current, im = img.current;
    if (!st || !im) return;
    const nw = im.naturalWidth || 1, nh = im.naturalHeight || 1;
    setSize({ w: nw, h: nh, fit: Math.min((st.clientWidth - 24) / nw, (st.clientHeight - 24) / nh) || 1 });
  }, []);
  useEffect(() => { setZ(1); setPan({ x: 0, y: 0 }); }, [index]);
  useEffect(() => {
    close.current?.focus();
    document.body.style.overflow = 'hidden';
    const k = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && index > 0) onIndex(index - 1);
      else if (e.key === 'ArrowRight' && index < figures.length - 1) onIndex(index + 1);
      else if (e.key === '+' || e.key === '=') setZ((x) => Math.min(3, x + 0.25));
      else if (e.key === '-') setZ((x) => Math.max(1, x - 0.25));
    };
    document.addEventListener('keydown', k);
    window.addEventListener('resize', fitNow);
    return () => { document.removeEventListener('keydown', k); window.removeEventListener('resize', fitNow); document.body.style.overflow = ''; };
  }, [index, figures.length, onIndex, onClose, fitNow]);
  const sc = size.fit * z;
  const W = stage.current?.clientWidth ?? 0, H = stage.current?.clientHeight ?? 0;
  const w = size.w * sc, h = size.h * sc;
  const mx = Math.max(0, (w - W) / 2), my = Math.max(0, (h - H) / 2);
  const px = Math.max(-mx, Math.min(mx, pan.x)), py = Math.max(-my, Math.min(my, pan.y));
  return (
    <div id="lb" role="dialog" aria-modal="true" aria-labelledby="lb-h" data-testid="lightbox">
      <button ref={close} type="button" className="portal-modal-close" id="lb-x" aria-label="Close dialog" onClick={onClose}>×</button>
      <div className="lb-bar">
        <h2 id="lb-h">{f.title}</h2>
        <button type="button" className="lb-btn" aria-label="Previous figure" disabled={index === 0} onClick={() => onIndex(index - 1)}>‹ Prev</button>
        <button type="button" className="lb-btn" aria-label="Next figure" disabled={index === figures.length - 1} onClick={() => onIndex(index + 1)}>Next ›</button>
        <button type="button" className="lb-btn" aria-label="Zoom out" disabled={z <= 1.001} onClick={() => zoom(z - 0.25)}>−</button>
        <span className="lb-zoom" aria-live="polite" data-testid="lb-zoom">{Math.round(z * 100)} %</span>
        <button type="button" className="lb-btn" aria-label="Zoom in" disabled={z >= 2.999} onClick={() => zoom(z + 0.25)}>+</button>
        <button type="button" className="lb-btn" aria-label="Fit to the window" onClick={() => { setPan({ x: 0, y: 0 }); setZ(1); }}>Fit</button>
      </div>
      <div className="lb-stage" ref={stage}
        onWheel={(e) => zoom(z * (e.deltaY < 0 ? 1.1 : 1 / 1.1))}
        onPointerDown={(e) => { drag.current = { x: e.clientX - px, y: e.clientY - py }; (e.currentTarget as HTMLElement).classList.add('drag'); }}
        onPointerMove={(e) => { if (drag.current) setPan({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y }); }}
        onPointerUp={(e) => { drag.current = null; (e.currentTarget as HTMLElement).classList.remove('drag'); }}
        onPointerLeave={(e) => { drag.current = null; (e.currentTarget as HTMLElement).classList.remove('drag'); }}>
        <img ref={img} src={f.src} alt={`${f.title} — ${f.purpose}`} onLoad={fitNow} draggable={false}
          style={size.w ? { position: 'absolute', left: 0, top: 0, width: w, height: h, transform: `translate(${((W - w) / 2 + px).toFixed(1)}px, ${((H - h) / 2 + py).toFixed(1)}px)` } : undefined} />
      </div>
      <div className="lb-cap"><b>{f.title}</b><span>Purpose — {f.purpose}</span><span className="lb-hint">Scroll to zoom to 300 %, drag to pan · ← → between the six figures · Escape closes</span></div>
    </div>
  );
}

export function WorkflowTab({ zone, ctx }: { zone: PortalZone; ctx: PageCtx }) {
  const toast = useToast();
  const [figs, setFigs] = useState<Figure[] | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [lb, setLb] = useState<number | null>(null);
  useEffect(() => {
    getWorkflow(zone).then((r) => setFigs(r.figures)).catch((err) => {
      const { title, detail } = toastFromError(err, 'Could not load the workflow figures');
      toast.push({ variant: 'error', title, detail });
    });
  }, [zone, toast]);
  const closeLb = useCallback(() => setLb(null), []);

  const save = async (f: Figure, v: { title: string; purpose: string; process: string }) => {
    setBusy(true);
    const settled = await Promise.allSettled(ZONES.map((z) => putFigure(z, f.figure, v)));
    setBusy(false);
    const i = ZONES.findIndex((z) => z.code === zone.code);
    const mine = settled[i];
    const failed = settled.map((s, j) => (s.status === 'rejected' ? ZONES[j]!.label : null)).filter(Boolean) as string[];
    if (mine?.status === 'fulfilled') setFigs((list) => list?.map((x) => (x.figure === f.figure ? mine.value : x)) ?? list);
    if (!failed.length) { toast.push({ variant: 'success', title: 'Figure words saved', detail: `${v.title} — saved in every zone.` }); setEditing(null); return; }
    const first = settled.find((s) => s.status === 'rejected') as PromiseRejectedResult;
    const t = toastFromError(first.reason, 'Could not save the figure words');
    toast.push({ variant: 'error', title: t.title, detail: `${t.detail ?? ''} Not saved in: ${failed.join(', ')}.`.trim() });
    if (mine?.status === 'fulfilled') setEditing(null);
  };

  return (
    <section id="tab-workflow">
      <div className="panel-step">
        <h3>The month's workflow <span className="portal-badge">BPMN 2.0 · STD-028</span></h3>
        <p className="panel-help" id="wf-lead">How a month of grading runs, from the report window closing to grades released — click a diagram to read it full size.</p>
        {!figs ? <Loading text="Loading the workflow figures…" /> : figs.map((f, i) => (
          <div key={f.figure}>
            <hr className="srule" aria-hidden="true" />
            <article className={`wf-item wf-card${editing === f.figure ? ' editing' : ''}`} data-fig={i} data-testid={`wf-card-${f.figure}`}>
              <header className="wf-head"><h4>{f.title}</h4>
                {ctx.can.configure && editing !== f.figure && (
                  <span className="wf-edit"><ActionIcon act="tedit" icon="edit" label={`Edit the words of ${figLabel(f)}`} tip="Edit the title, Purpose and Process — words only; the diagram stays" onClick={() => setEditing(f.figure)} /></span>
                )}
              </header>
              <dl className="wf-def"><dt>Purpose</dt><dd>{f.purpose}</dd><dt>Process</dt><dd>{f.process}</dd></dl>
              {editing === f.figure && <Editor fig={f} busy={busy} onCancel={() => setEditing(null)} onSave={(v) => void save(f, v)} />}
              {f.edited && editing !== f.figure && (
                <p className="wf-edited"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 20h4L19 9l-4-4L4 16z" /><path d="M13.5 6.5l4 4" /></svg>edited by {f.edited.by} · {fmtDate(f.edited.at)}</p>
              )}
              <figure className="wf-fig" title="Open full size" onClick={() => setLb(i)}>
                <img src={f.src} alt={`${f.title} — ${f.purpose}`} loading="lazy" />
                <button type="button" className="wf-open" aria-label={`Open ${figLabel(f)} full size`} onClick={(e) => { e.stopPropagation(); setLb(i); }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" /></svg>Open full size</button>
                <figcaption>{f.title.replace(' · ', ' — ')}.</figcaption>
              </figure>
            </article>
          </div>
        ))}
        <hr className="srule sec" aria-hidden="true" />
        <details className="wf-about" id="wf-about">
          <summary>About these diagrams</summary>
          <p>A dashed arrow is a message between two systems; a solid arrow is the next step inside one.</p>
          <p>Source: <code>Docs/Technical/grading-panel-workflow-reference.html</code>; the SVGs are served as static assets — this tab shows the committed files; it does not redraw them.</p>
        </details>
      </div>
      {figs && lb !== null && <Lightbox figures={figs} index={lb} onIndex={setLb} onClose={closeLb} />}
    </section>
  );
}
